<template>
    <article>
        <div class="article-content">
            <h2>List of {{ selectedYear }} respondents</h2>
            <p v-if="currentData">Thank you to the following {{ currentData.namedrespondents }} respondents in our survey. There were a further {{ currentData.unamedrespondents }} respondents that did not wish to be named.</p>
            <p v-else>No respondent data available for this year.</p>
            <ul v-if="currentRespondents.length">
                <li v-for="(respondent, index) in currentRespondents" :key="index">
                    {{ respondent }}
                </li>
            </ul>
        </div>
    </article>
</template>

<script setup lang="ts">
    import { ref, onMounted, computed, watch } from 'vue';
    import axios from 'axios';
    import { useRoute } from 'vue-router';
    import { useYear } from '../composables/useYear';
    import { useHead } from '@unhead/vue';
    import { BASE_URL } from '../config';


    interface RespondentData {
        namedrespondents: number;
        unamedrespondents: number;
        list: string[];
    }

    const { selectedYear } = useYear();
    const route = useRoute();

    // Sync year from route
    watch(() => route.params.year, (newYear) => {
        if (newYear && typeof newYear === 'string' && newYear !== selectedYear.value) {
            selectedYear.value = newYear;
        }
    }, { immediate: true });

    const allRespondents = ref<Record<string, RespondentData>>({});

    useHead({
        title: 'List of Respondents',
        meta: [
            { name: 'description', content: 'List of investment managers who responded to our survey.' },
            { property: 'og:title', content: 'List of Respondents - LCP Investment Fee Survey' },
            { property: 'og:description', content: 'List of investment managers who responded to our survey.' },
            { property: 'og:url', content: `${BASE_URL}/respondents` },
            { property: 'og:image', content: `${BASE_URL}/images/ifs-social.png` },
            { name: 'twitter:card', content: 'summary_large_image' },
            { name: 'twitter:title', content: 'List of Respondents - LCP Investment Fee Survey' },
            { name: 'twitter:description', content: 'List of investment managers who responded to our survey.' },
            { name: 'twitter:image', content: `${BASE_URL}/images/ifs-social.png` }
        ]
    })


    const currentData = computed(() => {
        return allRespondents.value[selectedYear.value] || null;
    });

    const currentRespondents = computed(() => {
        if (!currentData.value) return [];
        // Sort alphabetically for good measure
        return [...currentData.value.list].sort((a, b) => a.localeCompare(b));
    });

    onMounted(async () => {
        try {
            const { data } = await axios.get('/data/respondents.json');
            allRespondents.value = data;
        } catch (e) {
            console.error('Failed to load respondents', e);
        }
    });
</script>

<style lang="scss" scoped>
    .article-content {
        padding: 25px;
    }
    h2, h3, li span {
        font-weight: 700;
    }
    h2 {
        font-size: 1.5rem;
        font-weight: 700;
        padding: 0 0 20px 0;
        color: var(--lcp-blue);
        position: relative;
        margin-bottom: 20px;
        display: inline-block;

        // Horizontal underline with fade - responsive to text width
        &::before {
            content: '';
            position: absolute;
            bottom: 8px;
            left: 0;
            width: calc(100% - 10px);
            height: 3px;
            background: linear-gradient(to right,
                var(--investment-violet) 0%,
                var(--investment-violet) 50%,
                transparent 100%);
            border-radius: 2px;
        }

        // Small accent dot at start
        &::after {
            content: '';
            position: absolute;
            bottom: 5px;
            left: 0px;
            width: 9px;
            height: 9px;
            background: var(--investment-violet);
            border-radius: 50%;
        }
    }
    h3 {
        font-size: 1.1rem;
    }
    ul {
        list-style-type: disc;
        padding-left: 20px;
    }
    li {
        margin: 0 0 8px 0;
    }
    li::marker {
        color: #a250d6;
        font-size: 20px;
    }
    li:last-child {
        margin: 0;
    }

    a:active, a:visited {
        color: var(--lcp-blue);
    }
    a:hover {
        color: var(--dark-orange);
    }
    @media only screen and (max-width : 800px) and (orientation : portrait) {
        .left-pane, .right-pane {
            padding: 0;
        }
        .article-content {
            display: block;
            margin: 0;
            padding: 25px;
            overflow: hidden;
        }
        article {
            min-width: 100%;
            display: block;
            overflow: auto;
        }
    }
    @media only screen and (max-width : 480px) and (orientation : portrait),
    screen  and (max-width : 768px) and (orientation : landscape) {
        .article-content {
            padding: 15px;
        }
    }
</style>
